import create from 'zustand/vanilla';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getDDMLiveSchedule } from '@/services/matching.service';

export interface AddressInfo {
    type: 'domestic' | 'overseas';
    domestic: {
        name: string;
        phone: string;
        postCode: string;
        address: string;
        addressDetail: string;
    };
    overseas: {
        name: string;
        phone: string;
        addressLine1: string;
        addressLine2: string;
        city: string;
        stateProvince: string;
        postCode: string;
        country: string;
        phoneCountryCode: string;
    };
}

interface LiveScheduleState {
    currentSchedule: Awaited<ReturnType<typeof getDDMLiveSchedule>>[0] | null;
    selectedDate: string;
    selectedTime: string;
    isCreatingSalesLinks: boolean;
    selectedProducts: Set<string>;
    currentStep: number;
    maxReachedStep: number;
    selectedLocation: 'onsite' | 'studio' | 'avatar' | '';
    studioSubOption: 'ddm_internal' | 'external_studio' | '';
    selectedBuilding: string;
    selectedStudio: string;
}

interface LiveScheduleStatePatch {
    currentSchedule?: Awaited<ReturnType<typeof getDDMLiveSchedule>>[0] | null;
    selectedDate?: string;
    selectedTime?: string;
    isCreatingSalesLinks?: boolean;
    selectedProducts?: Set<string>;
    currentStep?: number;
    maxReachedStep?: number;
    selectedLocation?: 'onsite' | 'studio' | 'avatar' | '';
    studioSubOption?: 'ddm_internal' | 'external_studio' | '';
    selectedBuilding?: string;
    selectedStudio?: string;
}

export interface LiveScheduleStore extends LiveScheduleState {
    setCurrentSchedule: (schedule: Awaited<ReturnType<typeof getDDMLiveSchedule>>[0] | null) => void;
    patchCurrentSchedule: (schedule: LiveScheduleStatePatch) => void;
    setDateTime: (date: string, time: string) => void;
    setLocation: (
        location: 'onsite' | 'studio' | 'avatar' | '',
        subOption?: 'ddm_internal' | 'external_studio' | '',
    ) => void;
    setAddressInfo: (info: AddressInfo) => void;
    setBuilding: (buildingId: string) => void;
    setStudio: (studioId: string) => void;
    toggleProduct: (mapperId: string) => void;
    setSelectedProducts: (products: Set<string>) => void;
    setCurrentStep: (step: number) => void;
    setMaxReachedStep: (step: number) => void;
    setIsCreatingSalesLinks: (isCreating: boolean) => void;
    mergeStockRequests: (newRequests: any[]) => void;
    updateSampleRequestStatus: (sampleRequestId: string, status: string) => void;
    updateSampleRequest: (sampleRequestId: string, updates: any) => void;
    clearLiveSchedule: () => void;
}

const initializeState: LiveScheduleState = {
    currentSchedule: null,
    selectedDate: '',
    selectedTime: '18:00',
    isCreatingSalesLinks: false,
    selectedProducts: new Set(),
    currentStep: 0,
    maxReachedStep: 0,
    selectedLocation: '',
    studioSubOption: '',
    selectedBuilding: '',
    selectedStudio: '',
};

export const liveScheduleStore = create<LiveScheduleStore>()(
    persist(
        (set) => ({
            ...initializeState,

            setCurrentSchedule: (schedule) => {
                set({ currentSchedule: schedule });
            },

            patchCurrentSchedule: (patch) => {
                set((state) => ({
                    currentSchedule:
                        patch.currentSchedule !== undefined ? patch.currentSchedule : state.currentSchedule,
                    selectedDate: patch.selectedDate !== undefined ? patch.selectedDate : state.selectedDate,
                    selectedTime: patch.selectedTime !== undefined ? patch.selectedTime : state.selectedTime,
                    isCreatingSalesLinks:
                        patch.isCreatingSalesLinks !== undefined
                            ? patch.isCreatingSalesLinks
                            : state.isCreatingSalesLinks,
                    selectedProducts:
                        patch.selectedProducts !== undefined ? patch.selectedProducts : state.selectedProducts,
                    currentStep: patch.currentStep !== undefined ? patch.currentStep : state.currentStep,
                    maxReachedStep: patch.maxReachedStep !== undefined ? patch.maxReachedStep : state.maxReachedStep,
                    selectedLocation:
                        patch.selectedLocation !== undefined ? patch.selectedLocation : state.selectedLocation,
                    studioSubOption:
                        patch.studioSubOption !== undefined ? patch.studioSubOption : state.studioSubOption,
                    selectedBuilding:
                        patch.selectedBuilding !== undefined ? patch.selectedBuilding : state.selectedBuilding,
                    selectedStudio: patch.selectedStudio !== undefined ? patch.selectedStudio : state.selectedStudio,
                }));
            },

            setDateTime: (date, time) => {
                set({ selectedDate: date, selectedTime: time });
            },

            setLocation: (location, subOption = '') => {
                set({
                    selectedLocation: location,
                    studioSubOption: subOption,
                });
            },

            setAddressInfo: (info) => {
                set((state) => ({
                    currentSchedule: state.currentSchedule
                        ? {
                              ...state.currentSchedule,
                              addressInfo: info,
                          }
                        : null,
                }));
            },

            setBuilding: (buildingId) => {
                set({ selectedBuilding: buildingId, selectedStudio: '' });
            },

            setStudio: (studioId) => {
                set({ selectedStudio: studioId });
            },

            toggleProduct: (mapperId) => {
                set((state) => {
                    const newSet = new Set(state.selectedProducts);
                    if (newSet.has(mapperId)) {
                        newSet.delete(mapperId);
                    } else {
                        newSet.add(mapperId);
                    }
                    return { selectedProducts: newSet };
                });
            },

            setSelectedProducts: (products) => {
                set({ selectedProducts: products });
            },

            setCurrentStep: (step) => {
                set({ currentStep: step });
            },

            setMaxReachedStep: (step) => {
                set({ maxReachedStep: step });
            },

            setIsCreatingSalesLinks: (isCreating) => {
                set({ isCreatingSalesLinks: isCreating });
            },

            mergeStockRequests: (newRequests) => {
                set((state) => {
                    if (!state.currentSchedule) return state;

                    const existingRequests = state.currentSchedule.ddmStockRequests || [];
                    const existingMap = new Map(existingRequests.map((req: any) => [req.productId, req]));

                    // 신규 데이터로 업데이트 (같은 productId면 덮어쓰기)
                    newRequests.forEach((req: any) => {
                        existingMap.set(req.productId, req);
                    });

                    return {
                        currentSchedule: {
                            ...state.currentSchedule,
                            ddmStockRequests: Array.from(existingMap.values()),
                        },
                    };
                });
            },

            updateSampleRequestStatus: (sampleRequestId, status) => {
                set((state) => {
                    if (!state.currentSchedule) return state;

                    const updatedSampleRequests = state.currentSchedule.ddmSampleRequests?.map((req: any) =>
                        req.id === sampleRequestId ? { ...req, status } : req,
                    );

                    return {
                        currentSchedule: {
                            ...state.currentSchedule,
                            ddmSampleRequests: updatedSampleRequests,
                        },
                    };
                });
            },

            updateSampleRequest: (sampleRequestId, updates) => {
                set((state) => {
                    if (!state.currentSchedule) return state;

                    const updatedSampleRequests = state.currentSchedule.ddmSampleRequests?.map((req: any) =>
                        req.id === sampleRequestId ? { ...req, ...updates } : req,
                    );

                    return {
                        currentSchedule: {
                            ...state.currentSchedule,
                            ddmSampleRequests: updatedSampleRequests,
                        },
                    };
                });
            },

            clearLiveSchedule: () => {
                set(initializeState);
            },
        }),
        {
            name: 'live-schedule-storage',
            storage: createJSONStorage(() => sessionStorage),
            // Set은 JSON으로 직렬화할 수 없으므로 커스텀 serializer 필요
            // currentSchedule만 persist하고 UI 상태는 persist하지 않음
            partialize: (state) => ({
                currentSchedule: state.currentSchedule,
                selectedProducts: Array.from(state.selectedProducts),
            }),
            // deserialize 시 다시 Set으로 변환
            merge: (persistedState: any, currentState) => ({
                ...currentState,
                currentSchedule: persistedState?.currentSchedule || null,
                selectedProducts: new Set(persistedState?.selectedProducts || []),
            }),
        },
    ),
);

export default liveScheduleStore;
